/**
 * The document font catalogue.
 *
 * One list, read by the settings picker and by ThemeContext, so the options a
 * person sees are exactly the ones that have a `.font-doc-*` rule in index.css.
 * Each id there defines four custom properties — family, size, line-height and
 * tracking — which the editor and the rendered document both read. Nothing here
 * names a font-family: CSS owns the stacks, because the two editor layers have
 * to be styled by the same declaration or the text drifts out from under the
 * caret.
 */

export interface DocumentFontOption {
  id: string;
  name: string;
  description: string;
}

export const DOCUMENT_FONTS = [
  {
    id: "writerMono",
    name: "Writer",
    description: "Airy typewriter, slightly tightened",
  },
  {
    id: "sanFrancisco",
    name: "San Francisco",
    description: "Clear system sans",
  },
  {
    id: "sfMono",
    name: "SF Mono",
    description: "System monospace",
  },
  {
    id: "georgia",
    name: "Georgia",
    description: "Classic serif",
  },
  {
    id: "sourceSerif",
    name: "Source Serif",
    description: "Literary serif",
  },
  {
    id: "merriweather",
    name: "Merriweather",
    description: "Sturdy screen serif",
  },
] as const satisfies readonly DocumentFontOption[];

export type DocumentFont = (typeof DOCUMENT_FONTS)[number]["id"];

export const DEFAULT_DOCUMENT_FONT: DocumentFont = "writerMono";

/** Older catalogue ids → current ones, so a stored preference still resolves. */
const LEGACY_DOCUMENT_FONTS: Record<string, DocumentFont> = {
  inter: "sanFrancisco",
  robotoMono: "sfMono",
  robotoSlab: "georgia",
};

/** The body class that carries a font's custom properties. */
export function documentFontClass(id: DocumentFont): string {
  return `font-doc-${id}`;
}

/** Guards the value read back out of localStorage, which may predate this list. */
export function isDocumentFont(value: string | null): value is DocumentFont {
  return DOCUMENT_FONTS.some((font) => font.id === value);
}

/** Resolve a stored preference, including renamed legacy ids. */
export function resolveDocumentFont(value: string | null): DocumentFont {
  if (isDocumentFont(value)) return value;
  if (value && value in LEGACY_DOCUMENT_FONTS) {
    return LEGACY_DOCUMENT_FONTS[value];
  }
  return DEFAULT_DOCUMENT_FONT;
}
